import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Input } from '@/components/Input';
import { Loading } from '@/components/Loading';
import { UserRole, getUserRoleFromString } from '@/types/api';
import { userService } from '@/services/userService';
import type { User, UpdateUserRequest } from '@/types/api';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

/**
 * User Edit Page Component
 * 
 * Allows administrators to edit user information including roles and status.
 */
export function UserEdit() {
  useDocumentTitle('Edit User | SupportDesk');
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const populatedUserId = useRef<string | undefined>(undefined);
  const [saveError, setSaveError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: UserRole.EndUser,
    phoneNumber: '',
    department: '',
    jobTitle: '',
    isActive: true
  });

  // Fetch user data
  const {
    data: user,
    isLoading,
    error
  } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => userService.getUser(userId!),
    select: (response) => response.success ? response.data : null,
    enabled: !!userId
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: (updateData: UpdateUserRequest) => userService.updateUser(userId!, updateData),
    onSuccess: (response) => {
      if (response.success) {
        // Invalidate and refetch user data
        queryClient.invalidateQueries({ queryKey: ['users'] });
        queryClient.invalidateQueries({ queryKey: ['user', userId] });
        
        // Navigate back to user management
        navigate('/admin/users');
      } else {
        setSaveError(response.errors?.join(' ') || response.message || 'Unable to save this user. Please try again.');
      }
    },
    onError: (error) => {
      const failure = error as { message?: string; errors?: Record<string, string[]> };
      const details = failure.errors ? Object.values(failure.errors).flat().join(' ') : '';
      setSaveError(details || failure.message || 'Unable to save this user. Please try again.');
    }
  });

  // Populate form when user data is loaded
  useEffect(() => {
    if (user && populatedUserId.current !== user.id) {
      populatedUserId.current = user.id;
      const loadedUser = user as Omit<User, 'role'> & { phone?: string; role: UserRole | string };
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        role: typeof loadedUser.role === 'string' ? getUserRoleFromString(loadedUser.role) : loadedUser.role,
        phoneNumber: loadedUser.phone ?? user.phoneNumber ?? '',
        department: user.department || '',
        jobTitle: user.jobTitle || '',
        isActive: user.isActive ?? true
      });
    }
  }, [user]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);
    
    const updateData: UpdateUserRequest = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      phone: formData.phoneNumber,
      department: formData.department,
      jobTitle: formData.jobTitle,
      role: formData.role,
      isActive: formData.isActive
    };

    updateUserMutation.mutate(updateData);
  };

  const handleInputChange = (field: string, value: string | boolean | UserRole) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <Loading size="lg" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="space-y-6">
        <Card>
          <div className="p-6">
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error loading user</h3>
                  <p className="mt-1 text-sm text-red-700">
                    {error?.message || 'User not found or you do not have permission to edit this user.'}
                  </p>
                  <div className="mt-4">
                    <Button 
                      onClick={() => navigate('/admin/users')} 
                      variant="outline"
                      size="sm"
                    >
                      Back to Users
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white shadow">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="py-6 md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">
                Edit User
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Update user information and permissions
              </p>
            </div>
            <div className="mt-6 flex space-x-3 md:mt-0 md:ml-4">
              <Button 
                variant="outline"
                onClick={() => navigate('/admin/users')}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Form */}
      <Card>
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {saveError && (
              <div role="alert" className="rounded-md bg-red-50 p-4 text-sm text-red-700">
                {saveError}
              </div>
            )}
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <Input
                    id="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <Input
                    id="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    readOnly
                    aria-describedby="email-readonly-help"
                  />
                  <p id="email-readonly-help" className="mt-1 text-sm text-gray-500">
                    The sign-in email cannot be changed here.
                  </p>
                </div>

                <div>
                  <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <Input
                    id="phoneNumber"
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Role and Status */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Role and Status</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                    Role *
                  </label>
                  <select
                    id="role"
                    className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-sm"
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', parseInt(e.target.value) as UserRole)}
                    required
                  >
                    <option value={UserRole.EndUser}>End User</option>
                    <option value={UserRole.Agent}>Support Agent</option>
                    <option value={UserRole.Manager}>Manager</option>
                    <option value={UserRole.Admin}>Administrator</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={() => handleInputChange('isActive', true)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">Active</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="isActive"
                        checked={!formData.isActive}
                        onChange={() => handleInputChange('isActive', false)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">Inactive</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Professional Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Professional Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="department" className="block text-sm font-medium text-gray-700 mb-1">
                    Department
                  </label>
                  <Input
                    id="department"
                    type="text"
                    value={formData.department}
                    onChange={(e) => handleInputChange('department', e.target.value)}
                  />
                </div>

                <div>
                  <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700 mb-1">
                    Job Title
                  </label>
                  <Input
                    id="jobTitle"
                    type="text"
                    value={formData.jobTitle}
                    onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/users')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={updateUserMutation.isPending}
              >
                {updateUserMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
}
